/**
 * Admin console API.
 *
 * Mirrors engine src/routes/admin.js. Every endpoint here is behind
 * authenticate + requireAdmin on the engine; the route guard in this app is a
 * usability gate only — the engine is what actually enforces access.
 */

import { api, apiRequest } from './apiClient';
import type { Role } from '../auth/types';

// ── Compliance ──────────────────────────────────────────────────────────────

export const COMPLIANCE_ITEMS = [
  'venue_agreement', 'fire_marshal', 'alcohol_permit', 'insurance',
  'security_plan', 'ada_compliance', 'noise_ordinance', 'sales_tax',
] as const;

export type ComplianceItem = (typeof COMPLIANCE_ITEMS)[number];
export type ComplianceStatus = 'pending' | 'submitted' | 'approved' | 'not_required';

export const ITEM_LABELS: Record<ComplianceItem, string> = {
  venue_agreement: 'Venue agreement',
  fire_marshal: 'Fire marshal',
  alcohol_permit: 'Alcohol permit',
  insurance: 'Insurance',
  security_plan: 'Security plan',
  ada_compliance: 'ADA compliance',
  noise_ordinance: 'Noise ordinance',
  sales_tax: 'Sales tax',
};

export interface ComplianceEvent {
  eventId: string;
  title: string;
  eventStatus: string;
  eventDate: string | null;
  venue: string;
  createdAt: string;
  organizer: { userId: string; email: string; name: string };
  overallCompliant: boolean;
  checklistUpdatedAt: string | null;
  blockedFromPublishing: boolean;
  awaitingReview: number;
  outstanding: number;
  items: { item: ComplianceItem; status: ComplianceStatus; docUrl: string | null }[];
}

export const listCompliance = (filter: 'blocked' | 'ready' | 'all' = 'blocked') =>
  api.get<ComplianceEvent[]>(`/admin/compliance?filter=${filter}`);

/** Set one or more item statuses. The engine recomputes overall_compliant. */
export const approveCompliance = (
  eventId: string,
  items: Partial<Record<ComplianceItem, { status: ComplianceStatus }>>,
) =>
  api.post<{ eventId: string; overallCompliant: boolean; items: Record<string, unknown> }>(
    `/admin/events/${eventId}/compliance/approve`,
    { items },
  );

// ── Users ───────────────────────────────────────────────────────────────────

export interface AdminUser {
  userId: string;
  email: string;
  role: Role;
  name: string;
  phone: string | null;
  verified: boolean;
  suspended: boolean;
  suspensionReason: string | null;
  createdAt: string;
  /** null when the gate does not apply to this role. */
  investorApproved: boolean | null;
  artistApproved: boolean | null;
  artistProfileId: string | null;
}

export const listUsers = (params: {
  role?: string; search?: string; suspended?: boolean;
} = {}) => {
  const q = new URLSearchParams();
  if (params.role) q.set('role', params.role);
  if (params.search) q.set('search', params.search);
  if (params.suspended !== undefined) q.set('suspended', String(params.suspended));
  const query = q.toString();
  return api.get<AdminUser[]>(`/admin/users${query ? `?${query}` : ''}`);
};

export const userCounts = () =>
  api.get<{ role: Role; total: number; suspended: number }[]>('/admin/users/counts');

export const setSuspension = (userId: string, suspended: boolean, reason?: string) =>
  api.post<AdminUser>(`/admin/users/${userId}/suspension`, { suspended, reason });

/** The second half of the investor double gate. Deliberately its own call. */
export const approveInvestor = (userId: string) =>
  api.post<{ approved: boolean; wefunderInvestorId: string }>(
    `/admin/investors/${userId}/approve`,
  );

// ── Artists ─────────────────────────────────────────────────────────────────

export interface AdminArtist {
  artistProfileId: string;
  stageName: string;
  genre: string | null;
  bio: string | null;
  primaryMarket: string | null;
  profileImageUrl: string | null;
  instagramHandle: string | null;
  instagramFollowers: number | null;
  spotifyMonthlyListeners: number | null;
  tier: string | null;
  approved: boolean;
  approvedAt: string | null;
  createdAt: string;
  agreements: number;
  account: { userId: string; email: string; name: string; verified: boolean } | null;
}

export const listArtists = (approved: 'true' | 'false' | 'all' = 'false') =>
  api.get<AdminArtist[]>(`/admin/artists?approved=${approved}`);

export const setArtistApproval = (artistProfileId: string, approved: boolean) =>
  api.post<{ artistProfileId: string; stageName: string; approved: boolean }>(
    `/admin/artists/${artistProfileId}/approval`,
    { approved },
  );

// ── Financials ──────────────────────────────────────────────────────────────

export interface PlatformFinancials {
  gmv: {
    grossCents: number;
    completedOrders: number;
    ticketsSold: number;
    ticketsScanned: number;
  };
  events: { total: number; on_sale: number; draft: number; closed: number };
  settled: {
    events: number;
    grossCents: number;
    platformFeeCents: number;
    stripeFeeCents: number;
    netDistributableCents: number;
    note: string;
  };
  investments: { count: number; investedCents: number; platformEntryFeeCents: number };
}

export const getFinancials = () => api.get<PlatformFinancials>('/admin/financials');

// ── Audit log ───────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  ipAddress: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
  actor: { userId: string; email: string; role: Role } | null;
}

export const readAudit = (params: {
  action?: string; entityType?: string; entityId?: string;
  since?: string; until?: string; limit?: number; offset?: number;
} = {}) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  const query = q.toString();
  return api.get<{ total: number; entries: AuditEntry[] }>(
    `/admin/audit${query ? `?${query}` : ''}`,
  );
};

export const auditActions = () =>
  api.get<{ action: string; count: number }[]>('/admin/audit/actions');

// ── Order & ticket support (task 34) ────────────────────────────────────────

export interface SupportOrder {
  orderId: string;
  status: 'pending' | 'complete' | 'refunded' | 'partially_refunded' | 'expired';
  totalCents: number;
  refundedCents: number;
  netCents: number;
  refundedAt: string | null;
  refundReason: string | null;
  createdAt: string;
  paymentConfirmedAt: string | null;
  stripePaymentIntentId: string | null;
  stripeChargeId: string | null;
  /** Admin has full buyer access by design — see services/adminSupport.js. */
  buyer: {
    userId: string | null;
    name: string;
    email: string;
    phone: string | null;
    isGuest: boolean;
  };
  event: { eventId: string; title: string; date: string | null; venue: string };
}

export interface SupportTicket {
  ticketId: string;
  ticketUid: string;
  tierName: string | null;
  priceCents: number | null;
  status: string;
  hasQr: boolean;
  /** Already used at the door — matters before voiding. */
  scannedIn: boolean;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
}

export type SupportOrderDetail = SupportOrder & { tickets: SupportTicket[] };

export const searchOrders = (params: { search?: string; eventId?: string; status?: string } = {}) => {
  const q = new URLSearchParams();
  if (params.search?.trim()) q.set('search', params.search.trim());
  if (params.eventId) q.set('eventId', params.eventId);
  if (params.status) q.set('status', params.status);
  const query = q.toString();
  return api.get<SupportOrder[]>(`/admin/orders${query ? `?${query}` : ''}`);
};

export const getOrderDetail = (orderId: string) =>
  api.get<SupportOrderDetail>(`/admin/orders/${orderId}`);

/** Every mutation below requires a reason — it lands in the audit log. */
export const correctOrderEmail = (orderId: string, email: string, reason: string) =>
  apiRequest<{ email: string; previousEmail: string }>(`/admin/orders/${orderId}/email`, {
    method: 'PATCH', body: { email, reason },
  });

export const resendTicket = (orderId: string, reason: string) =>
  api.post<{ queued: boolean; sentTo: string }>(`/admin/orders/${orderId}/resend`, { reason });

export interface RefundResult {
  refundedCents: number;
  totalRefundedCents: number;
  remainingCents: number;
  status: string;
  ticketsVoided: number;
  stripeRefundId: string;
  settlementImpact: string;
}

/** Omit amountCents for a full refund of whatever remains. */
export const refundOrder = (orderId: string, reason: string, amountCents?: number) =>
  api.post<RefundResult>(`/admin/orders/${orderId}/refund`,
    amountCents == null ? { reason } : { reason, amountCents });

export interface CancelResult {
  status: string;
  previousStatus: string;
  inventoryReleased: boolean;
  wasScannedIn: boolean;
  qrInvalidated: boolean;
  refunded: boolean;
  note: string;
}

export const cancelTicket = (ticketId: string, reason: string) =>
  api.post<CancelResult>(`/admin/tickets/${ticketId}/cancel`, { reason });

// ── Investment opportunities (task 35) ──────────────────────────────────────

export type OpportunityStatus = 'draft' | 'open' | 'funded' | 'closed' | 'cancelled';

/** The entity that receives net proceeds for this raise's event. Null until attached. */
export interface AttachedSpv {
  id: string;
  legalEntityName: string;
  onboardingStatus: string;
  canReceiveTransfers: boolean;
}

export interface AdminOpportunity {
  /** '506c' (publicly solicitable) or '506b' (private network only). */
  offeringType: string;
  securityType: string;
  /** When solicitation began. Null means the offering has not started. */
  offeringStartDate: string | null;
  id: string;
  title: string;
  description: string | null;
  status: OpportunityStatus;
  totalShares: number;
  sharesSold: number;
  pricePerShareCents: number;
  minimumShares: number;
  investorPoolCents: number;
  totalEventCostCents: number;
  /**
   * The Manager capital this raise is SIZED for. A plan: it is set when the raise is
   * created, before any position exists. Compare with managerCapitalActualCents.
   */
  gpInvestmentCents: number;
  /** Same value, current name. The GP terminology is retired. */
  managerCapitalContributionCents: number;
  /** What the Manager has ACTUALLY contributed, summed from its Member positions. */
  managerCapitalActualCents: number;
  /** Outside investors. The Manager's own position is NOT counted here. */
  externalInvestorCount: number;
  /** Every Member position, the Manager's own included. */
  memberPositionCount: number;
  preferredReturnPercentage: number;
  /**
   * THE CARRY SETTLEMENT ACTUALLY USES. Reads investment_opportunities.gp_carry_percentage,
   * which is what runSettlement and the investor ledger both read.
   */
  gpCarryPercentage: number;
  /**
   * RETIRED by migration 062: the old per-investor fee, removed in 054. Kept on the read
   * so historical rows still display what they stated, and never written.
   */
  performanceFeePercentage: number;
  /**
   * Hero media for THIS RAISE, not the event. One event can carry several raises, so the
   * media belongs to the raise or both would share a hero. Null falls back to the event's.
   */
  featuredImageUrl: string | null;
  backgroundVideoUrl: string | null;
  backgroundVideoPosterUrl: string | null;
  /**
   * Read from the EVENT, not this raise. One event carries one venue rate however many
   * raises it has, because the venue is paid once from one pool of ticket revenue.
   */
  venueRevenueSharePercentage: number | null;
  closesAt: string | null;
  createdAt: string;
  updatedAt: string;
  wefunderSpvId: string | null;
  wefunderOpportunityUrl: string | null;
  investors: number;
  committedCents: number;
  /** People who registered interest. They block deletion, not cancellation. */
  leads: number;
  event: {
    eventId: string; title: string; status: string;
    date: string | null; city: string | null; compliant: boolean;
  };
  spv: AttachedSpv | null;
  canEdit: boolean;
  canOpen: boolean;
  canClose: boolean;
  /** Withdraw the raise. Off once anyone holds a contract, or once it is terminal. */
  canCancel: boolean;
  /** Remove the row entirely. Drafts with no investors and no leads only. */
  canDelete: boolean;
  /**
   * Why a draft cannot be opened. 'event_compliance_incomplete' is the common
   * one — publishing is gated on compliance, and opening requires a published
   * event, so the gate is transitive.
   */
  blockedReason:
    | 'spv_not_provisioned' | 'event_not_published' | 'event_compliance_incomplete'
  | 'spv_not_attached' | 'spv_onboarding_incomplete' | null;
}

export const listOpportunities = (status?: string) =>
  api.get<AdminOpportunity[]>(`/admin/opportunities${status ? `?status=${status}` : ''}`);

/** Mirrors the engine's CHECK constraints (migration 044). */
export const OFFERING_TYPES = ['506c', '506b'] as const;
export const SECURITY_TYPES = ['equity_units', 'revenue_share', 'convertible_note', 'safe'] as const;

export type OfferingType = (typeof OFFERING_TYPES)[number];
export type SecurityType = (typeof SECURITY_TYPES)[number];

export interface OpportunityInput {
  eventId: string;
  title: string;
  description?: string | null;
  totalShares: number;
  pricePerShareCents: number;
  /** Must equal totalShares × pricePerShareCents — the engine rejects anything else. */
  investorPoolCents: number;
  /** What the raise is sized for. The retired gpInvestmentCents name is no longer sent. */
  managerCapitalContributionCents: number;
  totalEventCostCents: number;
  minimumShares: number;
  preferredReturnPercentage: number;
  /**
   * The GP's carried interest on profit above the hurdle. THE ONLY FEE FIELD THAT DOES
   * ANYTHING. performanceFeePercentage is deliberately absent from this input: writing it
   * set a column settlement never reads, so the number an admin typed was discarded while
   * the real carry silently stayed at its default.
   */
  gpCarryPercentage: number;
  /**
   * A venue's percentage of ticketing revenue, taken after card processing and the platform
   * fee. STORED ON THE EVENT, not the raise: the venue is paid once out of one pool of
   * ticket revenue before anything splits, so an event carries one rate however many raises
   * it has. Requires a confirmed venue with working Connect onboarding.
   */
  venueRevenueSharePercentage?: number | null;
  featuredImageUrl?: string | null;
  backgroundVideoUrl?: string | null;
  backgroundVideoPosterUrl?: string | null;
  closesAt?: string | null;
  /** '506c' is publicly solicitable, '506b' is private network only. Defaults to 506c. */
  offeringType?: OfferingType;
  securityType?: SecurityType;
  /**
   * When solicitation begins. Null means it is stamped at the moment the raise is
   * opened, which is what most raises want. It matters most for 506(b): the gate admits
   * only investors whose relationship predates this date.
   */
  offeringStartDate?: string | null;
}

export interface CreatedOpportunity {
  id: string;
  status: string;
  offeringType: OfferingType;
  securityType: SecurityType;
  offeringStartDate: string | null;
}

// The engine stopped returning Wefunder identifiers in task 41a, when creating a raise
// stopped provisioning an SPV. The old type kept declaring them, which was a shape this
// endpoint had not returned for some time.
export const createOpportunity = (data: OpportunityInput) =>
  api.post<CreatedOpportunity>('/admin/opportunities', data);

export const updateOpportunity = (id: string, data: Partial<OpportunityInput>) =>
  api.put<{ id: string; investorPoolCents: number }>(`/admin/opportunities/${id}`, data);

export const openOpportunity = (id: string) =>
  api.post<{ id: string; status: string }>(`/admin/opportunities/${id}/open`);

export const closeOpportunity = (id: string, reason?: string) =>
  api.post<{ id: string; status: string; sharesSold: number; totalShares: number }>(
    `/admin/opportunities/${id}/close`, { reason });

/**
 * The event picker for the creation screen. Not /events/mine (an admin owns no
 * events) and not the public catalog (published only) — a raise may be drafted
 * against an event still clearing compliance.
 */
export interface SelectableEvent {
  eventId: string;
  title: string;
  status: string;
  eventDate: string | null;
  venue: string | null;
  organizer: { email: string; name: string | null };
  overallCompliant: boolean;
  existingOpportunityStatus: OpportunityStatus | null;
  canOpenOnceCreated: boolean;
}

export const searchEventsForOpportunity = (search?: string) =>
  api.get<SelectableEvent[]>(
    `/admin/opportunities/events${search ? `?search=${encodeURIComponent(search)}` : ''}`);

// ── Venue approval (task 39) ────────────────────────────────────────────────

/**
 * Mirrors AdminArtist. Note venue_profiles has no approved_at column, so unlike
 * artists there is no approval timestamp to show — the audit log carries when
 * and by whom.
 */
export interface AdminVenue {
  venueId: string;
  venueName: string;
  address: string | null;
  city: string | null;
  state: string | null;
  capacity: number | null;
  layoutOptions: string | null;
  technicalSpecs: string | null;
  pricingStructure: string | null;
  requirements: string | null;
  images: string[];
  approved: boolean;
  createdAt: string;
  /** Events pointed at this venue, and how many it has actually confirmed. */
  bookings: number;
  confirmedBookings: number;
  /** Whether the profile is a real room or a name and nothing else. */
  completeness: {
    hasCapacity: boolean;
    hasAddress: boolean;
    hasTechnicalSpecs: boolean;
    hasPricing: boolean;
    hasImages: boolean;
  };
  account: { userId: string; email: string; name: string; verified: boolean } | null;
  /**
   * The negotiated cut of gross ticket revenue for shows here, or null for no arrangement.
   *
   * PROSPECTIVE. It governs events published from now on. Events already published settle
   * at the rate frozen onto them when they went on sale, so changing this never re-prices
   * a show that is already selling tickets.
   */
  revenueSharePercentage: number | null;
  /** Whether that cut could actually be paid, or would defer at settlement. */
  payout: {
    stripeConnectAccountId: string | null;
    onboardingStatus: 'not_started' | 'pending' | 'complete' | 'restricted';
    payoutsEnabled: boolean;
    stripeDisabledReason: string | null;
    isMock: boolean;
    canReceiveTransfers: boolean;
  };
}

/** Whether an event can carry a venue revenue share, and what is already in force. */
export interface VenueShareEligibility {
  settable: boolean;
  reason:
    | null | 'locked_by_signature' | 'no_venue_attached' | 'venue_booking_not_confirmed'
    | 'onboarding_not_started' | 'account_restricted' | 'onboarding_incomplete'
    | 'venue_account_is_mock';
  currentRate: number | null;
  currentVenueId: string | null;
  signedInvestors: number;
  venueId?: string;
  venueName?: string;
}

export const getVenueShareEligibility = (eventId: string) =>
  api.get<VenueShareEligibility>(`/admin/opportunities/venue-share/${eventId}`);

export const listVenuesForReview = (approved: 'true' | 'false' | 'all' = 'false') =>
  api.get<AdminVenue[]>(`/admin/venues?approved=${approved}`);

export const setVenueApproval = (venueId: string, approved: boolean) =>
  api.post<{ venueId: string; venueName: string; approved: boolean }>(
    `/admin/venues/${venueId}/approval`, { approved });

/**
 * Set or clear a venue's revenue share. Admin only, by design: it is a negotiated
 * commercial term, not something a venue may award itself.
 *
 * `eventsAtFrozenRate` comes back so the screen can say how many already-published events
 * this change does NOT touch, rather than leaving an admin to assume it applies everywhere.
 */
export const setVenueRevenueShare = (venueId: string, revenueSharePercentage: number | null) =>
  api.patch<{
    id: string;
    venueName: string;
    revenueSharePercentage: number | null;
    eventsAtFrozenRate: number;
  }>(`/admin/venues/${venueId}/revenue-share`, { revenueSharePercentage });

/** Start or resume hosted Stripe Connect onboarding for a venue's payout account. */
export const startVenueOnboarding = (venueId: string, returnBase: string) =>
  api.post<{ url: string; expiresAt: number }>(
    `/admin/venues/${venueId}/onboarding`, { returnBase });

/** Ask Stripe what it currently thinks of the venue's account. */
export const refreshVenueOnboarding = (venueId: string) =>
  api.post<AdminVenue['payout'] & { id: string }>(`/admin/venues/${venueId}/refresh`, {});

// ── Investor approvals ──────────────────────────────────────────────────────
//
// Two pipelines, deliberately separate. An application comes from a registered investor
// who submitted accreditation and can be approved or declined. An inquiry comes from the
// public offering page, has no account behind it, and can only be marked as followed up.

export type InvestorApplicationState =
  | 'pending_review' | 'approved' | 'rejected' | 'not_submitted';

export interface InvestorApplication {
  userId: string;
  investorAccountId: string;
  email: string;
  name: string | null;
  registeredAt: string;
  state: InvestorApplicationState;
  accreditationMethod: string | null;
  accreditationDocUrl: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  reviewedBy: string | null;
  isTestPersona: boolean;
  canApprove: boolean;
  canReject: boolean;
}

export interface InvestorApplicationCounts {
  pendingReview: number;
  approved: number;
  rejected: number;
  notSubmitted: number;
}

export const listInvestorApplications = (state: string = 'pending_review') =>
  api.get<{ applications: InvestorApplication[]; counts: InvestorApplicationCounts }>(
    `/admin/investor-applications?state=${state}`);

export const approveInvestorApplication = (userId: string, note?: string | null) =>
  api.post<{ approved: boolean }>(`/admin/investors/${userId}/approve`, { note });

export const rejectInvestorApplication = (userId: string, reason: string) =>
  api.post<{ rejected: boolean }>(`/admin/investors/${userId}/reject`, { reason });

export interface InvestmentInquiry {
  id: string;
  name: string | null;
  email: string;
  kind: string;
  desiredAmountCents: number | null;
  selfAttestedAccredited: boolean;
  createdAt: string;
  contactedAt: string | null;
  contactedBy: string | null;
  opportunityTitle: string | null;
  userEmail: string | null;
}

export const listInvestmentInquiries = () =>
  api.get<InvestmentInquiry[]>('/admin/investor-inquiries');

export const markInquiryContacted = (id: string) =>
  api.post<{ contacted: boolean }>(`/admin/investor-inquiries/${id}/contacted`, {});

// ── Plugins ─────────────────────────────────────────────────────────────────

export interface InstalledPlugin {
  id: string | null;
  folder?: string;
  valid: boolean;
  error?: string;
  name?: string;
  version?: string;
  description?: string | null;
  hooks?: string[];
  permissions?: string[];
  enabled: boolean;
  /** Wired into the running process. Differs from `enabled` until the next restart. */
  active: boolean;
  installedAt?: string | null;
}

export const listPlugins = () => api.get<InstalledPlugin[]>('/admin/plugins');

export const setPluginEnabled = (id: string, enabled: boolean) =>
  api.post<{ id: string; enabled: boolean; appliesOnRestart: boolean }>(
    `/admin/plugins/${id}/enabled`, { enabled });

/**
 * Withdraw a raise nobody has committed to. The row stays and carries the outcome.
 *
 * Distinct from closeOpportunity, which is the end of a raise that ran and settles to
 * funded or closed depending on whether the shares sold. This one is the raise being
 * called off.
 */
export const cancelOpportunity = (id: string, reason?: string) =>
  api.post<{ id: string; status: string; previousStatus: string }>(
    `/admin/opportunities/${id}/cancel`, { reason });

/**
 * Remove a draft that never existed publicly. Irreversible.
 *
 * The engine refuses anything that is not a draft, or that has an investment or a
 * registered lead against it, so this cannot be used to erase a raise with a history.
 */
export const deleteOpportunity = (id: string) =>
  api.delete<{ id: string; deleted: boolean; title: string }>(`/admin/opportunities/${id}`);

/**
 * What this offering will still let an admin change.
 *
 * Served by the engine rather than inferred here, so the console's read-only state and
 * the server's refusal are computed from the same query. A UI that decides for itself
 * which fields are locked will eventually disagree with the rule that enforces it.
 */
export interface OfferingLockState {
  signedInvestors: number;
  hasSignedInvestor: boolean;
  hasSettled: boolean;
}

export const getOfferingLocks = (id: string) =>
  api.get<OfferingLockState>(`/admin/opportunities/${id}/locks`);

export interface DilutionRow {
  contractNumber: string;
  shares: number;
  ownershipBefore: number;
  ownershipAfter: number;
}

export interface RaiseSizeResult {
  applied: boolean;
  requiresAcknowledgement?: boolean;
  currentTotalShares: number;
  newTotalShares: number;
  unitsAdded: number;
  investorPoolCentsBefore: number;
  investorPoolCentsAfter: number;
  signedHolders: number;
  dilution: DilutionRow[];
}

/**
 * Increase the raise size. Without acknowledgeDilution this is a PREVIEW: the engine
 * computes each signed holder's ownership before and after and writes nothing.
 */
export const increaseRaiseSize = (
  id: string, newTotalShares: number, acknowledgeDilution = false, reason?: string,
) => api.post<RaiseSizeResult>(`/admin/opportunities/${id}/raise-size`, {
  newTotalShares, acknowledgeDilution, reason,
});
